import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { SiteChrome } from './components/SiteChrome';
import { DevToolbar } from './components/DevToolbar';
import { AdminShell } from './components/AdminShell';

// Lazy-load each screen into its own chunk. Per `docs/PLAN_digadop-ai-react-client.md`
// the wizard chunk target is ~50-100 KB gzipped; route-level splitting keeps
// the post-signup app code (when it lands) out of the prospect's first paint.
const Landing = lazy(() => import('./routes/Landing'));
const Redirecting = lazy(() => import('./routes/wizard/Redirecting'));
const Scanning = lazy(() => import('./routes/wizard/Scanning'));
const Quote = lazy(() => import('./routes/wizard/Quote'));
const OAuthDenied = lazy(() => import('./routes/wizard/OAuthDenied'));
const Welcome = lazy(() => import('./routes/wizard/Welcome'));
const Runs = lazy(() => import('./routes/app/Runs'));
const Documents = lazy(() => import('./routes/app/Documents'));
const Review = lazy(() => import('./routes/app/Review'));
const ReleaseNotes = lazy(() => import('./routes/app/ReleaseNotes'));
const ReleaseNote = lazy(() => import('./routes/app/ReleaseNote'));
const Personas = lazy(() => import('./routes/app/Personas'));
const Persona = lazy(() => import('./routes/app/Persona'));
const AuthGate = lazy(() => import('./routes/app/AuthGate'));

// Chrome (header + footer) is hidden on the scanning and quote screens to
// keep the prospect's eyes on the funnel, and on the whole /app portal, which
// carries its own Help-admin-style chrome (AdminShell) instead of the
// marketing header.
const HIDE_NAV_PATHS = ['/quote/scanning', '/quote/result', '/signup/welcome', '/app'];

function ChromeOrFull({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation();
  // The /app portal carries its own admin chrome (AdminShell); rendering it
  // inside SiteChrome left a second brand bar stacked above the shell.
  if (pathname.startsWith('/app')) {
    return (
      <>
        {children}
        {import.meta.env.DEV && <DevToolbar />}
      </>
    );
  }
  const hideNav = HIDE_NAV_PATHS.some((p) => pathname.startsWith(p));
  return (
    <SiteChrome hideNav={hideNav}>
      {children}
      {import.meta.env.DEV && <DevToolbar />}
    </SiteChrome>
  );
}

export default function App() {
  return (
    <ChromeOrFull>
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/quote/redirecting" element={<Redirecting />} />
          <Route path="/quote/scanning" element={<Scanning />} />
          {/* `:quoteId` is optional — present when arriving from the OAuth
              callback, absent when previewing tier cards via DevToolbar
              (which uses `?tier=`). */}
          <Route path="/quote/result" element={<Quote />} />
          <Route path="/quote/result/:quoteId" element={<Quote />} />
          <Route path="/quote/oauth-denied" element={<OAuthDenied />} />
          <Route path="/signup/welcome/:tenantId" element={<Welcome />} />
          {/* QA portal: tabbed admin (Recent Runs first) + HITL document review
              (see src/api/portal.ts), gated behind the Salesforce session
              (AuthGate) and wrapped in the Help-admin-style chrome. Review
              stays shell-less on purpose: eyes on the document. */}
          <Route path="/app" element={<Navigate to="/app/runs" replace />} />
          <Route path="/app/runs" element={<AuthGate><AdminShell><Runs /></AdminShell></AuthGate>} />
          <Route path="/app/documents" element={<AuthGate><AdminShell><Documents /></AdminShell></AuthGate>} />
          <Route path="/app/review/:docId" element={<AuthGate><Review /></AuthGate>} />
          {/* Release notes + change log (digadop-ai#40). */}
          <Route path="/app/release-notes" element={<AuthGate><AdminShell><ReleaseNotes /></AdminShell></AuthGate>} />
          <Route path="/app/release-notes/:noteId" element={<AuthGate><AdminShell><ReleaseNote /></AdminShell></AuthGate>} />
          {/* Personas maintenance (digadop-ai#36 v1). */}
          <Route path="/app/personas" element={<AuthGate><AdminShell><Personas /></AdminShell></AuthGate>} />
          <Route path="/app/personas/:personaId" element={<AuthGate><AdminShell><Persona /></AdminShell></AuthGate>} />
        </Routes>
      </Suspense>
    </ChromeOrFull>
  );
}

function RouteFallback() {
  return (
    <div className="px-6 py-32 text-center text-slate-400 text-sm">Loading…</div>
  );
}
